import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { ActionButton } from '@emerald-works-nova/components'
import React, { useMemo } from 'react'
import { useParams, useNavigate } from 'react-router'
import { Grid, Tab, Tabs, useTheme, CircularProgress, Box, Skeleton } from '@mui/material'
import AssignmentIcon from '@mui/icons-material/Assignment'
import BarChartIcon from '@mui/icons-material/BarChart'
import { BannerStatic, Panel, TabPanel, SectionHeader } from '../../components'
import Details from './components/details'
import Benchmark from './components/benchmark'
import { useStyles } from './style'
import { routes } from '../../routes'

export const IconMap = {
  details: <AssignmentIcon />,
  benchmark: <BarChartIcon />
}

const SectionMap = {
  details: {
    title: 'Details'
  },
  benchmark: {
    title: 'Benchmark'
  }
}

const AssessmentEdit = () => {
  const navigate = useNavigate()
  const classes = useStyles()
  const { spacing } = useTheme()
  const headerRef = React.useRef()
  const { assessmentId } = useParams()

  const [tabValue, setTabValue] = React.useState(Object.entries(SectionMap)[0][0])
  const [assessment, setAssessment] = React.useState({
    assessmentId: '',
    trialAssessmentId: '',
    title: '',
    metaSkills: [],
    benchmarkScores: []
  })

  const [getAssessmentConfig] = useEvent([
    {
      eventName: 'msaGetAssessmentConfig',
      onSuccess: (payload) => {
        if (payload) {
          setAssessment({
            assessmentId: payload.sk,
            title: payload.assessmentTitle,
            metaSkills: payload.metaSkills,
            benchmarkScores: payload.benchmarkScores,
            trialAssessmentId: payload.trialAssessmentId
          })
        }
      },
      onError: () => {
        BannerStatic.show({
          label: 'There was an error when getting the assessment details',
          isError: true
        })
        navigate(routes.assessmentsList.path)
      }
    }
  ])

  const [saveAssessmentConfig] = useEvent([
    {
      eventName: 'msaSaveAssessmentConfig',
      onSuccess: (payload) => {
        if (payload) {
          BannerStatic.show({
            label: 'Assessment details saved successfully.',
            isError: false
          })
        }
      },
      onError: () => {
        BannerStatic.show({
          label: 'Failed to save/update the assessment details. Please try again.',
          isError: true
        })
      }
    }
  ])

  const handleTabChange = React.useCallback((_, newValue) => setTabValue(newValue), [setTabValue])

  const handleAssessmentSave = () => {
    saveAssessmentConfig.trigger({
      assessmentExternalId: assessment.assessmentId,
      assessmentTitle: assessment.title,
      metaSkills: assessment.metaSkills,
      benchmarkScores: assessment.benchmarkScores
    })
  }

  useEventsOnViewLoad(() => {
    getAssessmentConfig.trigger({ assessmentExternalId: assessmentId })
  }, [getAssessmentConfig, assessmentId])

  const isLoading = getAssessmentConfig.isWorking || saveAssessmentConfig.isWorking
  const isSaveDisabled = useMemo(() => isLoading || !assessment, [isLoading, assessment])

  const sectionCompMap = {
    details: <Details assessment={assessment} setAssessment={setAssessment} />,
    benchmark: <Benchmark assessment={assessment} setAssessment={setAssessment} />
  }

  return (
    <Box>
      <Grid container spacing={2} style={{ marginBottom: spacing(3) }}>
        <Grid item xs={12} className={classes.controlBtnContainer}>
          <SectionHeader
            ref={headerRef}
            title={isLoading ? <Skeleton width={130} /> : assessment.title}
            location='Assessment /'
          />
          <ActionButton
            variant='primary'
            dataTest='assessment-save-button'
            onClick={handleAssessmentSave}
            disabled={isSaveDisabled}
          >
            {' '}
            Save
          </ActionButton>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        {/* Editor tabs */}
        <Grid item xs={12}>
          <Panel padding={0}>
            <Tabs
              indicatorColor='primary'
              textColor='primary'
              value={tabValue}
              onChange={handleTabChange}
              aria-label='Editor Tabs'
              classes={{ root: classes.tabs }}
            >
              {Object.entries(SectionMap).map(([key, section]) => (
                <Tab
                  id={`tab-${key}`}
                  aria-controls={`tabpanel-${key}`}
                  key={key}
                  value={key}
                  label={section.title}
                  icon={IconMap[key]}
                  classes={{ wrapper: classes.tabWrapper }}
                  data-test={`assessment-tab-${section.title}`}
                  disabled={isLoading}
                />
              ))}
            </Tabs>
            {/* Tabs content */}
            {Object.entries(SectionMap).map(([key]) => (
              <TabPanel key={key} value={tabValue} index={key}>
                {
                  isLoading
                    ? (
                      <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' height={300}>
                        <CircularProgress size={60} color='primary' />
                      </Box>
                    )
                    : sectionCompMap[key]
                }
              </TabPanel>
            ))}
          </Panel>
        </Grid>
      </Grid>
    </Box>
  )
}

export default AssessmentEdit
